import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import StepHtmlActivityBubble from "../ui/StepHtmlActivityBubble";

import type {
  HtmlActivityArtifactConfig,
  LearningBuddyActivityConfig,
  LearningBuddyArtifactConfig,
} from "../../types/chat";

type LearningBuddyActivityEvent = {
  widgetType: "coping_buddy" | "learning_buddy";
  widgetLabel: "Coping Buddy" | "Learning Buddy";
  activityKey: string;
  activityTitle: string;
  eventType:
    | "started"
    | "completed"
    | "used"
    | "saved"
    | "submitted";
  metadata?: Record<string, unknown>;
};

type Props = {
  artifact: LearningBuddyArtifactConfig;

  onActivityEvent?: (
    event: LearningBuddyActivityEvent
  ) => void;

  onHtmlActivityExpandedChange?: (
    expanded: boolean
  ) => void;
};

export type StepLearningBuddyBubbleHandle = {
  /**
   * Attempt to perform one of the currently available
   * Learning Buddy card selections from a spoken command.
   *
   * Returns the selected activity title when handled.
   * Returns null when the spoken text does not match
   * a supported current card action.
   */
  executeVoiceCommand: (
    raw: string
  ) => string | null;
};

function titleCase(value: string): string {
  if (!value) return "";

  return value.charAt(0).toUpperCase() + value.slice(1);
}

function normalizeVoiceChoice(
  value: string
): string {
  return String(value || "")
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[’]/g, "'")
  
    // Expand common spoken contraction before exact
    // Learning Buddy card matching.
    .replace(/\bwhat's\b/g, "what is")
  
    .replace(/[^a-z0-9\s']/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function activityUrlFor(
  activity: LearningBuddyActivityConfig
): string {
  const configuredUrl =
    typeof activity.config?.url === "string"
      ? activity.config.url
      : null;

  if (configuredUrl) return configuredUrl;

  switch (activity.activityKey) {
    case "understanding_suds":
      return "/activities/understanding-suds.html";

    case "what_is_erp":
      return "/activities/what_is_erp.html";

    case "erp_ocd_cycle_builder":
      return "/activities/ocd_cycle_builder.html";

    case "values_sort":
    case "values_card":
      return "/activities/values-sort.html";

    case "anxiety_body_map":
    case "anxiety_body_map_updated_v7":
      return "/activities/anxiety_body_map_updated_v7.html";

    default:
      return "";
  }
}

function buildHtmlActivityArtifact(
  activity: LearningBuddyActivityConfig,
  keyBase: string
): HtmlActivityArtifactConfig | null {
  const url = activityUrlFor(activity);

  if (!url) return null;

  return {
    id: `${activity.id}-${keyBase}`,
    stepId: "0",
    label: activity.title,
    hint: activity.subtitle,
    type: "html_activity",
    activityKey:
      activity.activityKey || activity.id,
    url,
  };
}

const StepLearningBuddyBubble =
  forwardRef<
    StepLearningBuddyBubbleHandle,
    Props
  >(
    (
      {
        artifact,
        onActivityEvent,
        onHtmlActivityExpandedChange,
      },
      ref
    ) => {
      // ------------------------------------------------------------
      // Available learning activities
      // ------------------------------------------------------------

      const activities = useMemo(() => {
        return Array.isArray(artifact.activities)
          ? artifact.activities.filter(
              (activity) =>
                activity.enabled !== false
            )
          : [];
      }, [artifact.activities]);

      const [
        selectedActivityId,
        setSelectedActivityId,
      ] = useState<string | null>(null);

      // ------------------------------------------------------------
      // Shared activity-selection path
      //
      // Mouse/touch and GPT-Live voice selection both call this.
      // ------------------------------------------------------------

      const selectActivity = useCallback(
        (activityId: string) => {
          setSelectedActivityId(activityId);
        },
        []
      );

      // ------------------------------------------------------------
      // Voice-selectable Learning Buddy cards
      //
      // This component does not know anything about GPT-Live itself.
      // It simply exposes the currently available local UI actions.
      //
      // ChatContainer decides whether/when to call this.
      // ------------------------------------------------------------

      useImperativeHandle(
        ref,
        () => ({
          executeVoiceCommand: (
            raw: string
          ): string | null => {
            const normalized =
              normalizeVoiceChoice(raw);

            if (!normalized) {
              return null;
            }

            for (const activity of activities) {
              const title =
                String(
                  activity.title || ""
                ).trim();

              if (!title) continue;

              const normalizedTitle =
                normalizeVoiceChoice(title);

              // Keep matching intentionally strict.
              //
              // We only intercept direct card-selection phrases.
              // Anything conversational, ambiguous, clinical, or
              // substantive should continue through GPT-Live → n8n.
              const aliases = new Set([
                normalizedTitle,
              
                `do ${normalizedTitle}`,
                `start ${normalizedTitle}`,
                `choose ${normalizedTitle}`,
                `select ${normalizedTitle}`,
              
                `i want ${normalizedTitle}`,
                `i want to do ${normalizedTitle}`,
              
                `lets do ${normalizedTitle}`,
                `let's do ${normalizedTitle}`,
              
                `can we do ${normalizedTitle}`,
                `can i do ${normalizedTitle}`,
              
                `please choose ${normalizedTitle}`,
              ]);
              
              // --------------------------------------------------
              // Known speech-to-text / natural-language variants
              // --------------------------------------------------
              //
              // Keep these explicit and deterministic.
              // Do NOT use fuzzy matching because a near-match should
              // still fall through to GPT-Live → n8n.
              //
              // Values Card Sort
              if (
                activity.activityKey === "values_sort" ||
                activity.activityKey === "values_card"
              ) {
                [
                  "values sort",
                  "value sort",
                  "values card sort",
                  "value card sort",
                  "values card",
                  "value card",
              
                  "do values card sort",
                  "do value card sort",
              
                  "start values card sort",
                  "start value card sort",
              
                  "choose values card sort",
                  "choose value card sort",
              
                  "select values card sort",
                  "select value card sort",
              
                  "lets do values card sort",
                  "let's do values card sort",
                  "lets do value card sort",
                  "let's do value card sort",
                ].forEach((alias) => {
                  aliases.add(
                    normalizeVoiceChoice(alias)
                  );
                });
              }
              
              // Anxiety Body Map
              if (
                activity.activityKey === "anxiety_body_map" ||
                activity.activityKey ===
                  "anxiety_body_map_updated_v7"
              ) {
                [
                  "anxiety body map",
              
                  // Common speech transcription variation:
                  "anxiety buddy map",
              
                  "body map",
                  "anxiety map",
              
                  "do anxiety body map",
                  "do anxiety buddy map",
              
                  "start anxiety body map",
                  "start anxiety buddy map",
              
                  "choose anxiety body map",
                  "choose anxiety buddy map",
              
                  "select anxiety body map",
                  "select anxiety buddy map",
              
                  "lets do anxiety body map",
                  "let's do anxiety body map",
                  "lets do anxiety buddy map",
                  "let's do anxiety buddy map",
                ].forEach((alias) => {
                  aliases.add(
                    normalizeVoiceChoice(alias)
                  );
                });
              }

              if (!aliases.has(normalized)) {
                continue;
              }

              selectActivity(activity.id);

              console.info(
                "[Learning Buddy][Voice] selected",
                {
                  transcript: raw,
                  activityId: activity.id,
                  title,
                }
              );

              return title;
            }

            return null;
          },
        }),
        [
          activities,
          selectActivity,
        ]
      );

      // ------------------------------------------------------------
      // Selected activity
      // ------------------------------------------------------------

      const selectedActivity =
        useMemo(() => {
          return (
            activities.find(
              (activity) =>
                activity.id ===
                selectedActivityId
            ) ?? null
          );
        }, [
          activities,
          selectedActivityId,
        ]);

      // ------------------------------------------------------------
      // HTML activity artifact
      // ------------------------------------------------------------

      const selectedHtmlActivity =
        useMemo(() => {
          if (!selectedActivity) {
            return null;
          }

          const sessionId =
            artifact.context?.sessionId ??
            null;

          const accessCode =
            artifact.context?.accessCode ??
            null;

          const keyBase =
            sessionId ||
            accessCode ||
            "learning-buddy";

          return buildHtmlActivityArtifact(
            selectedActivity,
            keyBase
          );
        }, [
          artifact.context,
          selectedActivity,
        ]);

      // ------------------------------------------------------------
      // Render
      // ------------------------------------------------------------

      return (
        <div className="mt-2 overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
          <div className="bg-gradient-to-br from-violet-700 via-violet-500 to-indigo-400 px-7 py-7 text-white shadow-sm sm:px-8 sm:py-8">
            <div className="text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
              Learning Buddy
            </div>

            <div className="mt-3 max-w-3xl text-base font-medium leading-7 text-white/95">
              Interactive tools to help you understand
              OCD, values, and recovery skills.
              Choose a learning activity and take it
              one step at a time.
            </div>
          </div>

          <div className="p-4">
            {activities.length > 0 ? (
              <>
                {/* Activity cards */}
                <div className="grid gap-3 sm:grid-cols-2">
                  {activities.map(
                    (activity) => {
                      const isSelected =
                        activity.id ===
                        selectedActivityId;

                      return (
                        <button
                          key={activity.id}
                          type="button"
                          onClick={() => {
                            selectActivity(
                              activity.id
                            );
                          }}
                          className={[
                            "rounded-2xl border p-4 text-left transition",
                            "focus:outline-none focus:ring-4 focus:ring-violet-100",

                            isSelected
                              ? "border-violet-300 bg-violet-50 shadow-sm"
                              : "border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white",
                          ].join(" ")}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="text-sm font-semibold text-slate-900">
                                {
                                  activity.title
                                }
                              </div>

                              {activity.subtitle ? (
                                <div className="mt-1 text-sm text-slate-600">
                                  {
                                    activity.subtitle
                                  }
                                </div>
                              ) : null}
                            </div>

                            {isSelected ? (
                              <div className="shrink-0 rounded-full bg-violet-600 px-2.5 py-1 text-xs font-medium text-white">
                                Selected
                              </div>
                            ) : null}
                          </div>

                          {activity.category ? (
                            <div className="mt-3 inline-flex rounded-full bg-white px-2.5 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
                              {titleCase(
                                activity.category
                              )}
                            </div>
                          ) : null}
                        </button>
                      );
                    }
                  )}
                </div>

                {/* Selected activity */}
                <div className="mt-4">
                  {selectedActivity?.renderType ===
                    "htmlActivity" &&
                  selectedHtmlActivity ? (
                    <StepHtmlActivityBubble
                      artifact={
                        selectedHtmlActivity as any
                      }
                      sessionId={
                        artifact.context
                          ?.sessionId ||
                        artifact.context
                          ?.accessCode ||
                        "learning-buddy"
                      }
                      accessCode={
                        artifact.context
                          ?.accessCode ||
                        undefined
                      }
                      onFinished={() => {}}
                      onActivityEvent={
                        onActivityEvent
                      }
                      onExpandedChange={
                        onHtmlActivityExpandedChange
                      }
                    />
                  ) : selectedActivity ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                      This learning activity is
                      not available yet.
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                      Select any card above to
                      begin.
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-sm text-slate-500">
                No learning activities are
                available yet.
              </div>
            )}
          </div>
        </div>
      );
    }
  );

StepLearningBuddyBubble.displayName =
  "StepLearningBuddyBubble";

export default StepLearningBuddyBubble;